import { useQuery } from '@apollo/client';
import {
  ActionGroup,
  ALL_SHARD_MODULES,
  DEFAULT_CLIENT_FIELDS_BY_COLUMN_NAME,
  getParsedCrudData,
  getTranslatedText,
  Loader,
  NoDataCard,
  TEXT_LENGTH,
  TooltipWrapper,
  useIsAuthorisedHook
} from '@nile/components';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { AccountClient, GET_FIELD } from '@nile/graphql/src';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  DisplayText,
  Heading,
  Page,
  ResourceItem,
  ResourceList,
  TextField,
  TextStyle,
  ColorsList
} from '@superops/ui-design-system';
import Tippy from '@tippyjs/react';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import nanoid from 'nanoid';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Select from 'react-select';
import envConfig from '@nile/home/src/Config/env';

const BadgeWithEditStatus = ({ status, onChange }) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = () => {
    setPopoverActive(!popoverActive);
  };
  const [colorData, setColorData] = useState(status);

  const onColorChange = (data) => {
    setColorData(data?.status);
    togglePopoverActive();
    if (onChange) {
      onChange(data?.status);
    }
  };

  return (
    <Tippy
      content={
        <div className="p20 p10">
          <div className="mb5">
            <TextStyle>Choose colors</TextStyle>
          </div>
          <ColorsList
            filterStatus={[
              'new',
              'secondary',
              'attention',
              'primary',
              'warningSecondary'
            ]}
            defaultStatus={colorData}
            onChange={onColorChange}
          />
        </div>
      }
      animation="scale-extreme"
      allowHTML
      visible={popoverActive}
      theme="light"
      appendTo={document.body}
      zIndex={3947483000}
      interactive
    >
      <div className="pointer flex actr" onClick={togglePopoverActive}>
        <Badge status={colorData} size="small">
          <div className="badge-color-cell"></div>
        </Badge>
      </div>
    </Tippy>
  );
};

const NewStatus = ({ onSave, order }) => {
  const intl = useIntl();
  const [value, setValue] = useState('');
  const [status, setStatus] = useState('default');
  const focusRef = React.useRef(false);

  const onValueChange = (updatedVal) => {
    setValue(updatedVal);
  };

  const updateStatus = (updatedStatus) => {
    setStatus(updatedStatus);
  };

  const onAdd = () => {
    if (value.trim() && value.length) {
      setStatus('default');
      setValue('');
      if (onSave) {
        onSave({
          id: nanoid(),
          value: value,
          order,
          details: {
            status: status
          }
        });
      }
    }
  };

  const handleKeyDown = (event) => {
    if (focusRef?.current && event.key === 'Enter' && !event.shiftKey) {
      onAdd();
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <TextField
        variant="outline"
        onChange={(val) => {
          onValueChange(val);
        }}
        placeholder={getTranslatedText(intl, 'common.new.entry')}
        value={value}
        prefixBorder
        prefix={
          <div className="flex actr ml-5">
            <BadgeWithEditStatus
              status={status}
              onChange={(val) => {
                updateStatus(val);
              }}
            />
          </div>
        }
        suffixBorder
        suffix={
          <div className="flex actr mr-5">
            <TooltipWrapper title="common.add">
              <Button plain onClick={onAdd} monochrome>
                <SVGIcon name="Add" />
              </Button>
            </TooltipWrapper>
          </div>
        }
        maxLength={TEXT_LENGTH.size_5}
        onFocus={() => {
          focusRef.current = true;
        }}
        onBlur={() => {
          focusRef.current = false;
        }}
      />
    </div>
  );
};

const EditMode = ({ item, onSave, onCancel, disableSort, status }) => {
  const intl = useIntl();
  const [value, setValue] = useState(item?.value);
  const focusRef = React.useRef(false);

  const [data, setData] = useState(item);
  const [badgeStatus, setBadgeStatus] = useState(status);

  const onChange = (val, key) => {
    setValue(val);
    const updatedItem = cloneDeep(data);
    if (updatedItem?.[key]) {
      updatedItem[key] = val || null;
    }
    setData(updatedItem);
  };

  const save = () => {
    const trimmedValue = data?.value && data?.value.trim();
    if (onSave && trimmedValue) {
      onSave({ ...data, value: trimmedValue });
    }
  };

  const cancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const onBadgeColorChange = (val) => {
    setBadgeStatus(val);
    setData((prevState) => ({
      ...prevState,
      details: {
        ...prevState?.details,
        status: val
      }
    }));
  };

  const handleKeyDown = (event) => {
    if (focusRef?.current && event.key === 'Enter' && !event.shiftKey) {
      save();
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <TextField
        variant="outline"
        placeholder={getTranslatedText(intl, 'common.edit.entry')}
        onChange={(val) => {
          onChange(val, 'value');
        }}
        value={value}
        prefix={
          <div className="flex g5 actr">
            {!disableSort && (
              <div className="flex">
                <SuperSortable.DragHandle />
              </div>
            )}
            <div className="flex actr">
              <BadgeWithEditStatus
                status={badgeStatus}
                onChange={onBadgeColorChange}
              />
            </div>
          </div>
        }
        suffix={
          <div className="flex g10 mr-5 actr">
            <TooltipWrapper title="common.save">
              <Button
                onClick={save}
                icon={<SVGIcon name="CircleTickThinOutline" />}
                plain
                iconColor="var(--sop-green-color)"
                monochrome
              />
            </TooltipWrapper>

            <TooltipWrapper title="common.cancel">
              <Button
                onClick={cancel}
                icon={<SVGIcon name="RejectedCircle" />}
                plainSecondary
                iconColor="var(--sops-text-critical)"
                plain
                monochrome
              />
            </TooltipWrapper>
          </div>
        }
        prefixBorder
        suffixBorder
        maxLength={TEXT_LENGTH.size_5}
        onFocus={() => {
          focusRef.current = true;
        }}
        onBlur={() => {
          focusRef.current = false;
        }}
      />
    </div>
  );
};
function Status(props) {
  const isEditAuthorised = useIsAuthorisedHook('clientCustomFields.edit');

  const [options, setOptions] = useState(null);
  const [parentOptions, setParentOptions] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [editOptionId, setEditOptionId] = useState(null);

  const handleAddChildren = (addedChild) => {
    const addedChildData = {
      ...addedChild,
      parentOption: {
        id: selectedParent?.id
      }
    };

    setOptions((prevOptions) => [...prevOptions, addedChildData]);
  };

  const handleDeleteChild = (deleteChildId) => {
    const updatedOptions = options.filter(
      (option) => option?.id !== deleteChildId
    );
    setOptions(updatedOptions);
  };

  const handleReorder = (updatedChildArray) => {
    const updatedOptions = options.map((option) => {
      const updatedOptionWithOrder = updatedChildArray.find(
        (el) => el?.id === option?.id
      );
      if (updatedOptionWithOrder) {
        return updatedOptionWithOrder;
      }
      return option;
    });
    setOptions(updatedOptions);
  };

  const handleChildrenUpdate = (updatedChild) => {
    const updatedOptions = options.map((option) => {
      if (updatedChild?.id === option?.id) {
        return updatedChild;
      }
      return option;
    });
    setOptions(updatedOptions);
  };

  const handleSelectedItems = (selectedItem) => {
    if (selectedItem?.length > 0) {
      const selectedItemId = selectedItem[selectedItem?.length - 1];
      const updatedSelection = parentOptions.find(
        (el) => el?.id === selectedItemId
      );
      setSelectedParent(updatedSelection);
      setEditOptionId(null);
    }
  };

  const {
    data: parentData,
    loading: parentLoading,
    error: parentError
  } = useQuery(GET_FIELD, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        entity: ALL_SHARD_MODULES.client,
        columnName: DEFAULT_CLIENT_FIELDS_BY_COLUMN_NAME.STAGE
      }
    },
    onCompleted({ getField }) {
      if (getField?.options) {
        setParentOptions(getField?.options);
        setSelectedParent(getField?.options?.[0]);
      }
    }
  });

  const { data, loading, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        entity: ALL_SHARD_MODULES.client,
        columnName: DEFAULT_CLIENT_FIELDS_BY_COLUMN_NAME.STATUS
      }
    },
    onCompleted({ getField }) {
      if (getField?.options) {
        setOptions(getField?.options);
      }
    }
  });

  const STATUS_MAP = {
    LOST: 'critical',
    NEGOTIATION: 'warning',
    NEW: 'info',
    WON: 'success',
    PAID: 'success',
    UNPAID: 'critical'
  };

  const getBadgeStatus = (details) => {
    if (details?.status) {
      return details?.status;
    }
    const constant = details?.constants;
    return STATUS_MAP?.[constant];
  };

  const onSave = () => {
    const base = data?.getField?.options;
    const parsedData = getParsedCrudData(base, options);
    const { addedItems, deletedItems, updatedItems } = parsedData;

    const fieldOptionsInput = {
      ...(addedItems?.length && {
        added: addedItems.map(({ value, order, details, parentOption }) => ({
          value,
          order,
          details,
          parentOption
        }))
      }),
      ...(deletedItems?.length && {
        deleted: deletedItems
      }),
      ...(updatedItems?.length && {
        updated: updatedItems,
        updateOptions: updatedItems
      })
    };

    let variables = { id: data?.getField?.id };

    if (!isEmpty(fieldOptionsInput)) {
      const optionsInput = {
        ...(addedItems?.length && {
          addOptions: addedItems.map(
            ({ value, order, details, parentOption }) => ({
              value,
              order,
              details,
              parentOption
            })
          )
        }),
        ...(deletedItems?.length && {
          deleteOptions: deletedItems.map(({ value, order, id }) => ({
            id
          }))
        }),
        ...(updatedItems?.length && {
          updateOptions: updatedItems.map(({ value, order, id, details }) => ({
            id,
            value,
            order,
            details
          }))
        })
      };
      variables = {
        ...variables,
        ...optionsInput
      };
      props.primaryAction(variables);
    } else {
      props?.secondaryAction();
    }
  };

  const selectedOptionIdArray = selectedParent?.id
    ? [selectedParent?.id]
    : null;

  const selectedOptionsData = useMemo(() => {
    if (!selectedParent?.id || isEmpty(options)) {
      return [];
    }

    const filtered = options.filter(
      (el) => el?.parentOption?.id === selectedParent.id
    );
    return sortBy(filtered, (el) => el?.order ?? 0);
  }, [options, selectedParent]);

  if (error) return <div>error occured </div>;

  return (
    <div className="full-height full-width">
      <div className="full-height full-width">
        <Page
          fullWidth
          variant="primary"
          contentHeight="140px"
          title={
            <Heading element="h1">
              <FormattedMessage id="attribute.status" />
            </Heading>
          }
          subtitle={
            <FormattedMessage id="admin.client.defaultField.status.description" />
          }
          primaryAction={
            <ActionGroup
              primaryText={<FormattedMessage id="common.save" />}
              primaryLoading={props.saveLoading}
              primaryAction={onSave}
              secondaryAction={props.secondaryAction}
              primaryDisabled={!isEditAuthorised}
            />
          }
        >
          {(loading || parentLoading) && !options ? (
            <>
              <Loader />
            </>
          ) : (
            <>
              <div className="flex actr g10 mb20">
                <div className="flex actr g5">
                  <div>
                    <SVGIcon name="DependentField" />
                  </div>
                  <div>
                    <TextStyle>
                      <FormattedMessage id="admin.customFields.dependent.field" />
                    </TextStyle>
                  </div>
                </div>
                <div className="w150p">
                  <Select placeholder="Stage" isDisabled />
                </div>
              </div>
              <div className="flex min-height-400 border br8">
                <div className="w300">
                  <div className="p1520">
                    <DisplayText element="h3" weight="SemiBold">
                      {data?.getField?.parentField?.label ||
                        'Available resources'}
                    </DisplayText>
                  </div>
                  <ResourceList
                    selectedItems={selectedOptionIdArray}
                    onSelectionChange={handleSelectedItems}
                    items={parentOptions || []}
                    renderItem={(item) => {
                      const { id, value } = item;
                      return (
                        <ResourceItem id={id} secondaryHoverBgColor>
                          <TextStyle>{value}</TextStyle>
                        </ResourceItem>
                      );
                    }}
                  />
                </div>
                <div className="border" />
                <div className="w90percent p1520">
                  <DisplayText element="h3" weight="SemiBold">
                    <TextStyle>Status</TextStyle>
                  </DisplayText>

                  <div className="full-width">
                    {isEditAuthorised && (
                      <div className="mt10">
                        <NewStatus
                          onSave={handleAddChildren}
                          order={selectedOptionsData?.length + 1}
                        />
                      </div>
                    )}

                    <div className="mbt20">
                      {isEmpty(selectedOptionsData) ? (
                        <div className="flex actr jctr w100 mt50">
                          <NoDataCard
                            imageURL={`url(${envConfig.imageRoot}svg/documents-empty-state.svg)`}
                            text={
                              <FormattedMessage id="app.selectionsheet.emptyState" />
                            }
                          />
                        </div>
                      ) : (
                        <div className="mt10">
                          <SuperSortable
                            onDragEnd={(reorderedList) => {
                              const reorderedItems = reorderedList.map(
                                (field, i) => ({
                                  ...field,
                                  order: i + 1
                                })
                              );
                              handleReorder(reorderedItems);
                            }}
                            list={selectedOptionsData}
                          >
                            {selectedOptionsData.map((child, idx) => {
                              const badgeStatus = getBadgeStatus(
                                child?.details
                              );
                              const isDeletable =
                                !child?.details?.constants || child?.deletable;
                              const value = child?.value;
                              const id = child?.id;
                              if (id === editOptionId) {
                                return (
                                  <EditMode
                                    item={child}
                                    onSave={(updatedChild) => {
                                      setEditOptionId(null);
                                      handleChildrenUpdate(updatedChild);
                                    }}
                                    onCancel={() => setEditOptionId(null)}
                                    status={badgeStatus}
                                  />
                                );
                              }
                              return (
                                <div className="hiddenContainer" key={id}>
                                  <ResourceItem secondaryHoverBgColor>
                                    <div className="flex actr fx-sb ">
                                      <div className="flex g10 actr">
                                        {isEditAuthorised && (
                                          <div className="flex">
                                            <SuperSortable.DragHandle />
                                          </div>
                                        )}
                                        <div className="flex">
                                          <Badge status={badgeStatus}>
                                            {value}
                                          </Badge>
                                        </div>
                                      </div>

                                      {isEditAuthorised && (
                                        <div>
                                          <div className="hiddenActions">
                                            <div className="flex g5">
                                              <TooltipWrapper title="common.edit">
                                                <Button
                                                  icon={<SVGIcon name="Edit" />}
                                                  plain
                                                  onClick={() => {
                                                    setEditOptionId(id);
                                                  }}
                                                />
                                              </TooltipWrapper>

                                              {isDeletable && (
                                                <TooltipWrapper title="common.delete">
                                                  <Button
                                                    icon={
                                                      <SVGIcon name="Delete" />
                                                    }
                                                    plain
                                                    onClick={() => {
                                                      handleDeleteChild(id);
                                                    }}
                                                  />
                                                </TooltipWrapper>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </ResourceItem>
                                </div>
                              );
                            })}
                          </SuperSortable>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Page>
      </div>
    </div>
  );
}

export default Status;
